
import React, { useContext, useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import imageCompression from 'browser-image-compression';
import { ThemeContext } from '../../hooks/ThemeContext';
import { UserContext } from '../../hooks/UserContext';
import { api } from '../../services/baseUrl';
import { Plus, Edit, Trash2, Eye, Power, Sparkles, Package2, FolderKanban, Archive } from 'lucide-react';
import ModalAdmin from '../../components/admin/ModalAdmin';
import SearchBar from '../../components/admin/SearchBar';
import Pagination from '../../components/admin/Pagination';
import Watermark from '../../components/common/Watermark';

const CreatorSpace = () => {

  // Hooks de contexte (doivent être déclarés AVANT toute fonction qui utilise user/role)
  const { theme } = useContext(ThemeContext);
  const { user, role } = useContext(UserContext);
  const isDark = theme === 'dark';

  // DEBUG : Affichage des infos utilisateur et rôle (après hooks)
  useEffect(() => {
    console.log('[DEBUG CreatorSpace] user:', user);
    console.log('[DEBUG CreatorSpace] role:', role);
  }, [user, role]);

  // Constantes globales du composant
  const itemsPerPage = 8; // Pagination : nombre d'éléments par page

  // ...hooks d'état et fonctions fetch...

  // Handler pour voir un produit (affichage en mode lecture seule)
  const handleViewProduct = (product) => {
    setProductFormMode('view');
    setSelectedProduct(product);
    setProductSaleType(product.sale_type || 'libre');
    if (product.sale_type === 'unique' && product.allowed_users) {
      setAllowedUsers(product.allowed_users.map(u => {
        if (typeof u === 'object') return String(u.id_user || u.id);
        return String(u);
      }));
    } else {
      setAllowedUsers([]);
    }
    setProductModalOpen(true);
  };

  // Handler pour supprimer un produit
  const handleDeleteProduct = async (product) => {
    if (!window.confirm(`Voulez-vous vraiment supprimer "${product.name}" ?`)) return;
    try {
      await api.delete(`/creator/products/${product.id_product || product.id}`);
      toast.success('Produit supprimé');
      fetchCreatorProducts();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast.error('Impossible de supprimer le produit');
    }
  };

      // Fonction pour charger les catégories (doit être déclarée avant les useEffect)
      const fetchCategories = async () => {
        try {
          const response = await api.get('/categories');
          let cats = response.data?.data || response.data || [];
          if (typeof cats === 'object' && !Array.isArray(cats)) {
            cats = Object.values(cats);
          }
          setCategories(Array.isArray(cats) ? cats : []);
        } catch (error) {
          console.error('Erreur catégories:', error);
          setCategories([]);
        }
      };
    // ========== FONCTIONS FETCH (remises pour éviter les erreurs no-undef) ==========
    const fetchAllUsers = React.useCallback(async () => {
      try {
        const response = await api.get('/users');
        const allUsers = response.data?.data || response.data || [];
        const currentId = user?.id_user || user?.id;
        const filteredUsers = allUsers.filter(u => (u.id_user || u.id) !== currentId);
        setUsers(filteredUsers);
      } catch (error) {
        console.error('Erreur utilisateurs:', error);
      }
    }, [user]);

    const fetchCreatorProjects = async () => {
      try {
        const response = await api.get('/projects/creator');
        const projectsData = response.data?.projects || [];
        setProjects(projectsData);
      } catch (error) {
        console.error('Erreur lors du chargement des projets:', error);
        toast.error('Impossible de charger les projets');
      }
    };

  // (Suppression des redéfinitions de hooks en double)

  // Hooks d'état et refs (toujours en haut du composant)
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [searchProduct, setSearchProduct] = useState('');
  const [currentPageProducts, setCurrentPageProducts] = useState(1);
  const [totalPagesProducts, setTotalPagesProducts] = useState(1);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productFormMode, setProductFormMode] = useState('create');
  const [uploadedImages, setUploadedImages] = useState([]); // [{file, preview}]
  const [productFiles, setProductFiles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productSaleType, setProductSaleType] = useState('libre');
  const [allowedUsers, setAllowedUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [showArchives, setShowArchives] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [priceInputValue, setPriceInputValue] = useState('');
  const fileInputRef = useRef(null);
  const stockInputRef = useRef(null);
  const [projects, setProjects] = useState([]);
  const [searchProject, setSearchProject] = useState('');
  const [currentPageProjects, setCurrentPageProjects] = useState(1);
  const [totalPagesProjects, setTotalPagesProjects] = useState(1);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectFormMode, setProjectFormMode] = useState('create');
  const [projectLinkType, setProjectLinkType] = useState('client');
  const [projectFormData, setProjectFormData] = useState({
    id_user: '',
    id_creator: '',
    name: '',
    type: 'Personnalisé',
    status: 'En cours',
    progress: 0,
    description: '',
    start_date: '',
    end_date: '',
  });

  // Définir toutes les fonctions fetch AVANT les useEffect


  const fetchCreatorProducts = async () => {
      // DEBUG : Affichage du rôle et de l'utilisateur lors du fetch
      console.log('[DEBUG fetchCreatorProducts] role:', role, 'user:', user);
    try {
      let response;
      if (role === 1 || role === '1' || user?.is_admin) {
        // Admin : tous les produits
            response = await api.get(`/admin/products`);
      } else if (role === 2 || role === '2') {
        // Créateur : uniquement ses produits
            response = await api.get(`/creator/products`, { params: { id_user: user?.id_user || user?.id } });
      } else {
        setProducts([]);
        setFilteredProducts([]);
        setTotalPagesProducts(1);
        setLoadingProducts(false);
        return;
      }
      const raw = response.data?.data || response.data || [];
      const mapped = raw.map((p) => {
          // DEBUG : Affichage de chaque produit brut reçu
          console.log('[DEBUG Produit brut]', p);
        const allowed = (p.allowed_users || p.allowedUsers || []).map((u) => {
          if (typeof u === 'object') return String(u.id_user || u.id);
          return String(u);
        });
        // Harmonisation image principale
        let imageData = null;
        if (p.images && Array.isArray(p.images) && p.images.length > 0) {
          imageData = p.images[0];
        }
        // Utilise resolveProductImageUrl pour l'image principale
        const image = resolveProductImageUrl({
          ...p,
          path_watermarked: imageData?.path_watermarked,
          path_image: imageData?.path_image,
        });
        return {
          id: p.id_product ?? p.id,
          id_product: p.id_product ?? p.id,
          id_user: p.id_user ?? p.creator_id ?? '',
          name: p.name ?? p.name_product ?? '',
          category: p.category?.name ?? p.category ?? p.category_name ?? '',
          price: p.price_ttc ?? p.price ?? p.price_product ?? 0,
          price_ht: p.price ?? p.price_product ?? 0,
          stock: p.available_stock ?? p.stock ?? 0,
          sale_type: p.sale_type ?? '',
          status: p.status ?? '',
          description: p.description ?? p.description_product ?? '',
          image,
          images: p.images || [],
          path_watermarked: imageData?.path_watermarked || null,
          path_image: imageData?.path_image || null,
          created_at: p.created_at ?? '',
          allowed_users: allowed,
        };
      });
      setProducts(mapped);
        // DEBUG : Affichage des produits mappés
        console.log('[DEBUG Produits mappés]', mapped);
      setFilteredProducts(mapped);
      setTotalPagesProducts(Math.max(1, Math.ceil(mapped.length / itemsPerPage)));
    } catch (error) {
      console.error('Erreur produits:', error);
      toast.error('Impossible de charger vos produits');
    } finally {
      setLoadingProducts(false);
    }
  };



  // useEffect toujours en haut, jamais dans une condition !
  // TOUS les useEffect doivent être AVANT tout if/return !
  useEffect(() => { fetchCategories(); }, []);
  useEffect(() => {
    if (activeTab !== 'products') return;
    if (!user) return;

    if (role === 1 || role === '1' || role === 2 || role === '2' || user?.is_admin || user?.id_role === 1 || user?.id_role === 2) {
      setLoadingProducts(true);
      fetchCreatorProducts();
    }
  }, [activeTab, role, user]);
    useEffect(() => {
      // DEBUG : Affichage des projets reçus
      console.log('[DEBUG Projets]', projects);
    }, [projects]);
  useEffect(() => { fetchAllUsers(); }, [fetchAllUsers]);
  useEffect(() => { fetchCreatorProjects(); }, []);
  useEffect(() => {
    setLoadingProducts(true);
    const query = searchProduct.toLowerCase();
    const filtered = products.filter((p) => {
      const normalizedStatus = String(p.status || '').toLowerCase();
      const isArchivedStatus = normalizedStatus === 'inactive' || normalizedStatus === 'archived';
      const matchesStatus = showArchives ? isArchivedStatus : !isArchivedStatus;
      const matchesQuery = query
        ? (p.name || '').toLowerCase().includes(query) ||
          (p.category || '').toLowerCase().includes(query)
        : true;

      return matchesStatus && matchesQuery;
    });

    setFilteredProducts(filtered);
    setTotalPagesProducts(Math.max(1, Math.ceil(filtered.length / itemsPerPage)));
    setCurrentPageProducts(1);
    setTimeout(() => setLoadingProducts(false), 100);
  }, [searchProduct, products, showArchives, user, role]);
  useEffect(() => {
    const query = searchProject.toLowerCase();
    const filtered = projects.filter(p =>
      (p.name || '').toLowerCase().includes(query) ||
      (p.type || '').toLowerCase().includes(query) ||
      (p.user?.name || '').toLowerCase().includes(query) ||
      (p.user?.email || '').toLowerCase().includes(query)
    );
    setTotalPagesProjects(Math.max(1, Math.ceil(filtered.length / itemsPerPage)));
    setCurrentPageProjects(1);
  }, [projects, searchProject]);

  // Restriction d'accès : admin (id_role 1), créateur (id_role 2) ou user.is_admin
  const isAdmin = user && (user.is_admin || user.id_role === 1 || role === 1 || role === '1');
  const isCreator = user && (user.id_role === 2 || role === 2 || role === '2');


  // Ce hook doit être appelé AVANT tout return/if, et une seule fois !
  useEffect(() => {
    if (user && (user.is_admin || user.id_role === 1 || role === 1 || role === '1') && activeTab === 'products') {
      setActiveTab('projects');
    }
  }, [user, role, activeTab]);

  if (!user || (!isAdmin && !isCreator)) {
    return (
      <div className={`flex-1 flex flex-col items-center justify-center min-h-[60vh] ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
        <h1 className="text-2xl font-bold mb-4">Accès refusé</h1>
        <p className="text-lg">Vous n'avez pas accès à cet espace.</p>
      </div>
    );
  }

  // Handler pour la création d'un produit
  const handleCreateProduct = () => {
    setProductFormMode('create');
    setSelectedProduct(null);
    setProductSaleType('libre');
    setAllowedUsers([]);
    setSelectedCategory('');
    setPriceInputValue('');
    setUploadedImages([]);
    setProductFiles([]);
    setProductModalOpen(true);
  };

  // Handler pour la sauvegarde d'un projet (création ou édition)
  const handleSaveProject = async (e) => {
    e.preventDefault();
    try {
      if (!projectFormData.name || !projectFormData.id_user) {
        toast.error('Veuillez remplir tous les champs obligatoires.');
        return;
      }
      const payload = {
        ...projectFormData,
        progress: Number(projectFormData.progress) || 0,
      };
      if (projectFormMode === 'create') {
        await api.post('/projects', payload);
        toast.success('Projet créé !');
      } else {
        await api.put(`/projects/${selectedProject?.id_project || selectedProject?.id}`, payload);
        toast.success('Projet modifié !');
      }
      setProjectModalOpen(false);
      fetchCreatorProjects();
    } catch (error) {
      console.error('Erreur sauvegarde projet:', error);
      const errorMsg = error.response?.data?.message || error.response?.data?.errors || error.message;
      toast.error('Erreur: ' + JSON.stringify(errorMsg));
    }
  };

  // Résoudre l'URL publique d'une image produit (priorité watermark, via route protégée)
  const resolveProductImageUrl = (p) => {
    if (!p) return null;
    // Priorité: path_watermarked > path_image > autres
    const path = p.path_watermarked || p.path_image || p.image || p.image_url || p.picture_product || (p.images && p.images[0] && (p.images[0].path_watermarked || p.images[0].path_image));
    if (!path) return null;

    const apiBaseUrl = api?.defaults?.baseURL || 'https://api.krayneagency.fr/api';
    const apiOrigin = apiBaseUrl.replace(/\/api\/?$/, '');

    if (String(path).startsWith('http')) {
      if (String(path).includes('/api/images/')) {
        try {
          const parsedUrl = new URL(String(path));
          return `${apiOrigin}${parsedUrl.pathname}`;
        } catch {
          return String(path);
        }
      }

      return String(path);
    }
    
    // Les images sont servies via la route protégée /api/images/{path}
    let cleanPath = String(path).replace(/^\/+/, ''); // retirer les / au début
    if (cleanPath.startsWith('storage/')) {
      cleanPath = cleanPath.substring(8); // retirer 'storage/'
    }
    
    // Utiliser la route protégée de l'API
    const finalUrl = `${apiOrigin}/api/images/${cleanPath}`;
    return finalUrl;
  };





  const handleEditProduct = (product) => {
    setProductFormMode('edit');
    setSelectedProduct(product);
    setProductSaleType(product.sale_type || 'libre');
    setPriceInputValue(String(product.price_ht ?? product.price ?? ''));
    
    // Définir la catégorie sélectionnée
    const categoryName = typeof product.category === 'object' 
      ? (product.category?.name || product.category?.name_category || '')
      : (product.category || '');
    setSelectedCategory(categoryName);
    
    // Charger les utilisateurs autorisés si vente unique
    if (product.sale_type === 'unique' && product.allowed_users) {
      setAllowedUsers(product.allowed_users.map(u => {
        if (typeof u === 'object') return String(u.id_user || u.id);
        return String(u);
      }));
    } else {
      setAllowedUsers([]);
    }
    setUploadedImages([]);
    setProductFiles([]);
    setProductModalOpen(true);
  };
    // Suppression de handleImageUpload et finalFile inutilisés

  const handleToggleProductStatus = async (product) => {
    try {
      const newStatus = product.status === 'active' ? 'inactive' : 'active';
      await api.patch(`/creator/products/${product.id_product || product.id}/status`, {
        status: newStatus,
        id_user: user?.id_user || user?.id
      });
      
      // Mettre à jour le statut localement
      setProducts(products.map(p => 
        (p.id_product || p.id) === (product.id_product || product.id) 
          ? { ...p, status: newStatus }
          : p
      ));
      
      toast.success(`Produit ${newStatus === 'active' ? 'activé' : 'désactivé'}`);
    } catch (error) {
      console.error('Erreur toggle statut:', error);
      toast.error('Impossible de changer le statut du produit');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        console.log('Image sélectionnée:', file.name, 'Taille:', (file.size / 1024 / 1024).toFixed(2), 'MB');
        
        // Options de compression
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true
        };
        
        // Compresser l'image
        const compressedFile = await imageCompression(file, options);
        console.log('Image compressée:', compressedFile.name, 'Taille:', (compressedFile.size / 1024 / 1024).toFixed(2), 'MB');
        
        // Normaliser en File (le compresseur peut retourner un Blob)
        const finalFile = compressedFile instanceof File
          ? compressedFile
          : new File([compressedFile], file.name, { type: file.type || 'image/jpeg' });

        // Stocker le fichier compressé
        
        // Créer un aperçu
        const reader = new FileReader();
        reader.onloadend = () => {
        };
        reader.readAsDataURL(compressedFile);
      } catch (error) {
        console.error('Erreur lors de la compression:', error);
        toast.error('Erreur lors du traitement de l\'image');
      }
    }
  };

  const handleProductFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    setProductFiles(files);
  };

  const handleSaveProduct = async (productData) => {
    try {
      // Validation: vérifier qu'au moins une image est fournie pour la création
      if (productFormMode === 'create' && uploadedImages.length === 0) {
        toast.error('Veuillez sélectionner au moins une image pour créer un produit');
        return;
      }

      // Validation: vérifier qu'UN utilisateur est sélectionné pour les ventes uniques
      if (productData.sale_type === 'unique' && allowedUsers.length === 0) {
        toast.error('Veuillez sélectionner UN utilisateur pour cette vente unique');
        return;
      }
      if (productData.sale_type === 'unique' && allowedUsers.length > 1) {
        toast.error('Une vente unique ne peut avoir qu\'UN SEUL acheteur');
        return;
      }

      const formData = new FormData();
      formData.append('name', productData.name);
      formData.append('category', productData.category);
      formData.append('price', productData.price);
      formData.append('stock', productData.stock);
      formData.append('sale_type', productData.sale_type);
      formData.append('description', productData.description || '');

      // Ajouter les utilisateurs autorisés pour ventes uniques
      if (productData.sale_type === 'unique' && allowedUsers.length > 0) {
        allowedUsers.forEach((userId) => {
          formData.append('allowed_users[]', userId);
        });
      }

      // Gestion des images multiples
      if (uploadedImages.length > 0) {
        uploadedImages.forEach(({ file }, idx) => {
          formData.append('images[]', file, file.name || `image${idx}.jpg`);
        });
      } else if (productFormMode === 'edit') {
        // Pas de nouvelle image, conserver les existantes côté backend
      }

      if (productFiles.length > 0) {
        productFiles.forEach((file) => {
          formData.append('files[]', file);
        });
      }

      if (productFormMode === 'create') {
        await api.post('/creator/products', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Produit créé ! En attente de validation par un admin.');
      } else {
        formData.append('_method', 'PUT');
        await api.post(`/creator/products/${selectedProduct.id_product || selectedProduct.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Produit modifié !');
      }

      setProductModalOpen(false);
      setProductFiles([]);
      fetchCreatorProducts();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      const errorMsg = error.response?.data?.message || error.response?.data?.errors || error.message;
      toast.error('Erreur: ' + JSON.stringify(errorMsg));
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'Actif';
      case 'inactive':
        return 'Inactif';
      case 'pending':
        return 'En attente';
      default:
        return status;
    }
  };

  // ========== FONCTIONS PROJETS ==========
  

  const handleCreateProject = () => {
    setProjectFormMode('create');
    setSelectedProject(null);
    setProjectLinkType('client');
    setProjectFormData({
      id_user: '',
      id_creator: '',
      name: '',
      type: 'Personnalisé',
      status: 'En cours',
      progress: 0,
      description: '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
    });
    setProjectModalOpen(true);
  };

  const handleEditProject = (project) => {
    const currentUserId = user?.id_user || user?.id;
    const isCreatorOwner = currentUserId && String(project.id_user) === String(currentUserId);
    setProjectFormMode('edit');
    setSelectedProject(project);
    setProjectLinkType(isCreatorOwner ? 'creator' : 'client');
    setProjectFormData({
      id_user: project.id_user,
      id_creator: project.id_creator || '',
      name: project.name || '',
      type: project.type || 'Personnalisé',
      status: project.status || 'En cours',
      progress: project.progress || 0,
      description: project.description || '',
      start_date: project.start_date || '',
      end_date: project.end_date || '',
    });
    setProjectModalOpen(true);
  };

  // Multi-image upload handler
  const handleImagesUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const newImages = await Promise.all(files.map(async (file) => {
      try {
        const options = { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true };
        const compressedFile = await imageCompression(file, options);
        const finalFile = compressedFile instanceof File ? compressedFile : new File([compressedFile], file.name, { type: file.type || 'image/jpeg' });
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve({ file: finalFile, preview: reader.result });
          };
          reader.readAsDataURL(finalFile);
        });
      } catch (error) {
        toast.error('Erreur lors du traitement d\'une image');
        return null;
      }
    }));
    setUploadedImages((prev) => [...prev, ...newImages.filter(Boolean)]);

    if (e.target) {
      e.target.value = '';
    }
  };

  // Supprimer une image localement avant envoi
  const handleRemoveUploadedImg = (idx) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== idx));

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  // (bloc vide supprimé, code déplacé ou corrigé)

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce projet ?')) {
      return;
    }
    
    try {
      await api.delete(`/projects/${projectId}`);
      toast.success('Projet supprimé');
      fetchCreatorProjects();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast.error('Impossible de supprimer le projet');
    }
  };

  // ========== FIN FONCTIONS PROJETS ==========

  // Filtrage et pagination des projets
  // (useEffect déplacé en haut)

  const displayedProducts = filteredProducts.slice(
    (currentPageProducts - 1) * itemsPerPage,
    currentPageProducts * itemsPerPage
  );

  const filteredProjects = projects.filter(p =>
    (p.name || '').toLowerCase().includes(searchProject.toLowerCase()) ||
    (p.type || '').toLowerCase().includes(searchProject.toLowerCase()) ||
    (p.user?.name || '').toLowerCase().includes(searchProject.toLowerCase()) ||
    (p.user?.email || '').toLowerCase().includes(searchProject.toLowerCase()) ||
    (p.creator?.name || '').toLowerCase().includes(searchProject.toLowerCase()) ||
    (p.creator?.email || '').toLowerCase().includes(searchProject.toLowerCase())
  );

  const displayedProjects = filteredProjects.slice(
    (currentPageProjects - 1) * itemsPerPage,
    currentPageProjects * itemsPerPage
  );

  const creatorDisplayName = user?.nickname || user?.firstname || 'Créateur';
  const activeProductsCount = products.filter((product) => (product.status || '').toLowerCase() === 'active').length;
  const archivedProductsCount = products.filter((product) => (product.status || '').toLowerCase() !== 'active').length;
  const pendingProductsCount = products.filter((product) => (product.status || '').toLowerCase() === 'pending').length;
  const statCards = [
    {
      label: 'Produits actifs',
      value: activeProductsCount,
      icon: Package2,
      accent: isDark ? 'from-emerald-500/20 to-teal-500/10' : 'from-emerald-100 to-teal-50',
      iconClass: 'text-emerald-500',
    },
    {
      label: 'Archives',
      value: archivedProductsCount,
      icon: Archive,
      accent: isDark ? 'from-amber-500/20 to-orange-500/10' : 'from-amber-100 to-orange-50',
      iconClass: 'text-amber-500',
    },
    {
      label: 'Projets clients',
      value: projects.length,
      icon: FolderKanban,
      accent: isDark ? 'from-violet-500/20 to-fuchsia-500/10' : 'from-violet-100 to-fuchsia-50',
      iconClass: 'text-violet-500',
    },
    {
      label: 'En attente',
      value: pendingProductsCount,
      icon: Sparkles,
      accent: isDark ? 'from-rose-500/20 to-pink-500/10' : 'from-rose-100 to-pink-50',
      iconClass: 'text-rose-500',
    },
  ];
  const parsedPriceHT = Number.parseFloat(priceInputValue || selectedProduct?.price_ht || selectedProduct?.price || 0) || 0;
  const platformCommission = parsedPriceHT * 0.05;
  const creatorPayout = parsedPriceHT * 0.95;

  return (
    <div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className={`relative mb-8 overflow-hidden rounded-[28px] border p-6 sm:p-8 ${isDark ? 'border-white/10 bg-[#0b0b0c]' : 'border-white/70 bg-white/95 shadow-[0_20px_60px_rgba(15,23,42,0.08)]'}`}>
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -right-14 top-0 h-40 w-40 rounded-full bg-rose-500/15 blur-3xl" />
            <div className="absolute left-0 top-1/2 h-32 w-32 -translate-y-1/2 rounded-full bg-violet-500/10 blur-3xl" />
          </div>
          <div className="relative grid gap-6 lg:grid-cols-[1.5fr_0.9fr] lg:items-center">
            <div>
              <div className={`mb-4 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium uppercase tracking-[0.22em] ${isDark ? 'border-white/10 bg-white/5 text-rose-300' : 'border-rose-200 bg-rose-50 text-rose-600'}`}>
                <Sparkles className="h-3.5 w-3.5" />
                Espace Créateur
              </div>
              <h1 className="text-3xl font-bold leading-tight sm:text-4xl">Espace Créateur</h1>
              <p className={`mt-3 max-w-2xl text-sm sm:text-base ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Gérez vos produits, vos livrables et vos projets clients depuis une interface plus claire, plus premium et plus rapide.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                <span className={`rounded-full px-3 py-1 text-xs font-medium ${isDark ? 'bg-white/5 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>
                  {activeProductsCount} produits actifs
                </span>
                <span className={`rounded-full px-3 py-1 text-xs font-medium ${isDark ? 'bg-white/5 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>
                  {projects.length} projets suivis
                </span>
                <span className={`rounded-full px-3 py-1 text-xs font-medium ${isDark ? 'bg-rose-500/15 text-rose-200' : 'bg-rose-50 text-rose-600'}`}>
                  {pendingProductsCount} en attente de validation
                </span>
              </div>
            </div>

            <div className={`rounded-[24px] border p-5 ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-100 bg-white/80'}`}>
              <p className={`text-xs font-semibold uppercase tracking-[0.22em] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Vue d'ensemble
              </p>
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Compte</span>
                  <span className="font-semibold">{creatorDisplayName}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Onglet actif</span>
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${isDark ? 'bg-rose-500/15 text-rose-300' : 'bg-rose-50 text-rose-600'}`}>
                    {activeTab === 'products' ? 'Produits' : 'Projets Clients'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Mode affichage</span>
                  <span className="font-semibold">{showArchives ? 'Archives' : 'Actifs'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {statCards.map((card) => {
            const Icon = card.icon;

            return (
              <div
                key={card.label}
                className={`rounded-[22px] border p-4 ${isDark ? 'border-white/10 bg-[#0b0b0c]' : 'border-white/70 bg-white shadow-[0_12px_40px_rgba(15,23,42,0.06)]'}`}
              >
                <div className={`mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br ${card.accent}`}>
                  <Icon className={`h-5 w-5 ${card.iconClass}`} />
                </div>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{card.label}</p>
                <p className="mt-1 text-2xl font-bold">{card.value}</p>
              </div>
            );
          })}
        </div>

        {/* Onglets */}
        <div className={`mb-6 flex flex-wrap gap-2 rounded-2xl border p-2 ${isDark ? 'border-white/10 bg-[#0b0b0c]' : 'border-white/70 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.05)]'}`}>
          {isCreator && (
            <button
              onClick={() => setActiveTab('products')}
              className={`rounded-xl px-5 py-3 font-medium transition-all relative ${
                activeTab === 'products'
                  ? isDark
                    ? 'bg-rose-500/15 text-rose-300 shadow-[inset_0_0_0_1px_rgba(244,63,94,0.25)]'
                    : 'bg-rose-50 text-rose-600 shadow-[inset_0_0_0_1px_rgba(251,113,133,0.25)]'
                  : isDark
                    ? 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
              }`}
            >
              Produits
            </button>
          )}
          <button
            onClick={() => setActiveTab('projects')}
            className={`rounded-xl px-5 py-3 font-medium transition-all relative ${
              activeTab === 'projects'
                ? isDark
                  ? 'bg-rose-500/15 text-rose-300 shadow-[inset_0_0_0_1px_rgba(244,63,94,0.25)]'
                  : 'bg-rose-50 text-rose-600 shadow-[inset_0_0_0_1px_rgba(251,113,133,0.25)]'
                : isDark
                  ? 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
            }`}
          >
            Projets Clients
          </button>
        </div>

        {/* Contenu - Produits */}
        {activeTab === 'products' && (
          <>
        {/* Actions */}
        <div className={`mb-6 flex flex-col gap-4 rounded-[24px] border p-4 lg:flex-row lg:items-center lg:justify-between ${isDark ? 'border-white/10 bg-[#0b0b0c]' : 'border-white/70 bg-white shadow-[0_12px_40px_rgba(15,23,42,0.06)]'}`}>
          <div className="flex flex-col gap-3 lg:min-w-[360px] lg:max-w-xl lg:flex-1">
            <div>
              <h2 className="text-lg font-semibold">Catalogue créateur</h2>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Retrouvez vos visuels, gérez les statuts et pilotez vos ventes depuis cette vue.
              </p>
            </div>
            <div className="flex items-center gap-2">
            <SearchBar
              value={searchProduct}
              onChange={setSearchProduct}
              placeholder="Rechercher vos produits..."
            />
            <button
              onClick={() => setShowArchives(!showArchives)}
              className={`px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
                showArchives
                  ? isDark ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-500 text-white hover:bg-blue-600'
                  : isDark ? 'bg-white/10 text-gray-400 hover:bg-white/20' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              title={showArchives ? 'Afficher actifs' : 'Afficher archives'}
            >
              {showArchives ? '📦 Archives' : '✓ Actifs'}
            </button>
          </div>
          </div>
          <button
            onClick={handleCreateProduct}
            className="flex items-center justify-center gap-2 rounded-xl bg-rose-500 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-rose-600 lg:self-end"
          >
            <Plus className="w-4 h-4" />
            Nouveau produit
          </button>
        </div>

        {/* Table */}
        {loadingProducts ? (
          <div className={`text-center py-12 rounded-lg ${isDark ? 'bg-[#0b0b0c] border border-white/10' : 'bg-white border border-gray-200'}`}>
            <p className="text-sm opacity-70">Chargement des produits…</p>
          </div>
        ) : displayedProducts.length === 0 ? (
          <div className={`text-center py-12 rounded-lg ${isDark ? 'bg-[#0b0b0c] border border-white/10' : 'bg-white border border-gray-200'}`}>
            <div className="flex flex-col items-center justify-center gap-2">
              <svg className="w-10 h-10 mb-2 text-blue-400 opacity-60" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m6 4a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm opacity-70 font-medium">
                {showArchives ? 'Aucun produit archivé pour le moment' : 'Aucun produit actif pour le moment'}
              </p>
              <button
                onClick={handleCreateProduct}
                className="mt-4 px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 text-sm"
              >
                Créer votre premier produit
              </button>
            </div>
          </div>
        ) : (
          <div className={`rounded-lg border overflow-hidden ${isDark ? 'bg-[#0b0b0c] border-white/10' : 'bg-white border-gray-200'}`}>
            <table className="w-full">
              <thead>
                <tr className={`border-b ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Image</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Nom</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Catégorie</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Prix HT</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Stock</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Statut</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayedProducts.map((product) => (
                  <tr key={product.id} className={`border-b ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'} transition-colors`}>
                    <td className="px-6 py-4 text-sm">
                      {resolveProductImageUrl(product) && (
                        <Watermark>
                          <img
                            src={resolveProductImageUrl(product)}
                            alt={product.name}
                            className="max-h-10 rounded"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        </Watermark>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">{product.name}</td>
                    <td className="px-6 py-4 text-sm">{product.category}</td>
                    <td className="px-6 py-4 text-sm">{product.price_ht}€</td>
                    <td className="px-6 py-4 text-sm">{product.stock === 0 ? 'Illimité' : product.stock}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`px-2 py-1 rounded text-xs ${
                        product.status === 'active' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
                      }`}>
                        {getStatusLabel(product.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewProduct(product)}
                          className={`p-2 rounded transition ${isDark ? 'hover:bg-blue-500/10 text-blue-500' : 'hover:bg-blue-50 text-blue-600'}`}
                          title="Voir"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditProduct(product)}
                          className={`p-2 rounded transition ${isDark ? 'hover:bg-yellow-500/10 text-yellow-400' : 'hover:bg-yellow-50 text-yellow-600'}`}
                          title="Modifier"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleProductStatus(product)}
                          className={`p-2 rounded transition ${product.status === 'active' 
                            ? isDark ? 'hover:bg-green-500/10 text-green-500' : 'hover:bg-green-50 text-green-600'
                            : isDark ? 'hover:bg-orange-500/10 text-orange-500' : 'hover:bg-orange-50 text-orange-600'
                          }`}
                          title={product.status === 'active' ? 'Désactiver' : 'Activer'}
                        >
                          <Power className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(product)}
                          className={`p-2 rounded transition ${isDark ? 'hover:bg-red-500/10 text-red-500' : 'hover:bg-red-50 text-red-600'}`}
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPagesProducts > 1 && (
          <Pagination
            currentPage={currentPageProducts}
            totalPages={totalPagesProducts}
            onPageChange={setCurrentPageProducts}
          />
        )}
          </>
        )}

        {/* Contenu - Projets */}
        {activeTab === 'projects' && (
          <>
            {/* Actions */}
            <div className={`mb-6 flex flex-col gap-4 rounded-[24px] border p-4 lg:flex-row lg:items-center lg:justify-between ${isDark ? 'border-white/10 bg-[#0b0b0c]' : 'border-white/70 bg-white shadow-[0_12px_40px_rgba(15,23,42,0.06)]'}`}>
              <div className="flex flex-col gap-3 lg:min-w-[360px] lg:max-w-xl lg:flex-1">
                <div>
                  <h2 className="text-lg font-semibold">Projets Clients</h2>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Suivez vos livrables, la progression et les échanges liés à chaque mission.
                  </p>
                </div>
                <SearchBar
                  value={searchProject}
                  onChange={setSearchProject}
                  placeholder="Rechercher un projet..."
                />
              </div>
              <button
                onClick={handleCreateProject}
                className="flex items-center justify-center gap-2 rounded-xl bg-rose-500 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-rose-600 lg:self-end"
              >
                <Plus className="w-4 h-4" />
                Nouveau projet
              </button>
            </div>

            {/* Table */}
            {displayedProjects.length === 0 ? (
              <div className={`text-center py-12 rounded-lg ${isDark ? 'bg-[#0b0b0c] border border-white/10' : 'bg-white border border-gray-200'}`}>
                <div className="flex flex-col items-center justify-center gap-2">
                  <svg className="w-10 h-10 mb-2 text-blue-400 opacity-60" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m6 4a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm opacity-70 font-medium">Aucun projet pour le moment</p>
                  <button
                    onClick={handleCreateProject}
                    className="mt-4 px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 text-sm"
                  >
                    Créer votre premier projet
                  </button>
                </div>
              </div>
            ) : (
              <div className={`rounded-lg border overflow-hidden ${isDark ? 'bg-[#0b0b0c] border-white/10' : 'bg-white border-gray-200'}`}>
                <table className="w-full">
                  <thead>
                    <tr className={`border-b ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Nom</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Client</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Type</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Statut</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Progression</th>
                      <th className="px-6 py-4 text-right text-sm font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedProjects.map((project) => (
                      <tr key={project.id_project} className={`border-b ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <td className="px-6 py-4 text-sm font-medium">{project.name}</td>
                        <td className="px-6 py-4 text-sm">
                          {project.user ? (
                            <div>
                              <p className="font-medium">{project.user.name || project.user.first_name}</p>
                              <p className="text-xs opacity-70">{project.user.email}</p>
                            </div>
                          ) : (
                            <span className="text-gray-500">N/A</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm">{project.type}</td>
                        <td className="px-6 py-4 text-sm">
                          <span className={`px-2 py-1 rounded text-xs ${
                            project.status === 'Terminé' 
                              ? 'bg-green-500/20 text-green-500'
                              : project.status === 'En cours'
                                ? 'bg-blue-500/20 text-blue-500'
                                : project.status === 'En attente'
                                  ? 'bg-yellow-500/20 text-yellow-500'
                                  : 'bg-gray-500/20 text-gray-500'
                          }`}>
                            {project.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div className="flex items-center gap-3">
                            <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                              <div 
                                className="bg-rose-500 h-2 rounded-full transition-all" 
                                style={{ width: `${project.progress || 0}%` }}
                              />
                            </div>
                            <span className="text-xs font-medium w-10">{project.progress || 0}%</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleEditProject(project)}
                              className={`p-2 rounded transition ${isDark ? 'hover:bg-yellow-500/10 text-yellow-400' : 'hover:bg-yellow-50 text-yellow-600'}`}
                              title="Modifier"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteProject(project.id_project)}
                              className={`p-2 rounded transition ${isDark ? 'hover:bg-red-500/10 text-red-500' : 'hover:bg-red-50 text-red-600'}`}
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPagesProjects > 1 && (
              <Pagination
                currentPage={currentPageProjects}
                totalPages={totalPagesProjects}
                onPageChange={setCurrentPageProjects}
              />
            )}
          </>
        )}

        {/* Modal Produit */}
        <ModalAdmin
          isOpen={productModalOpen}
          onClose={() => {
            setProductModalOpen(false);
            setUploadedImages([]);
            setProductFiles([]);
            setPriceInputValue('');
          }}
          title={productFormMode === 'create' ? 'Créer un produit' : productFormMode === 'edit' ? 'Modifier le produit' : 'Détails du produit'}
          size="lg"
        >
          {productFormMode !== 'view' ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSaveProduct({
                  name: e.target.elements.name.value,
                  category: selectedCategory,
                  price: parseFloat(e.target.elements.price.value),
                  stock: parseInt(e.target.elements.stock.value),
                  sale_type: e.target.elements.sale_type.value,
                  description: e.target.elements.description.value,
                });
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Nom <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    defaultValue={selectedProduct?.name || ''}
                    required
                    className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Catégorie <span className="text-rose-500">*</span>
                  </label>
                  <select
                    name="category"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    required
                    className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200'}`}
                  >
                    <option value="">-- Sélectionner --</option>
                    {categories.map(cat => (
                      <option key={cat.id_category || cat.id} value={cat.name || cat.name_category}>
                        {cat.name || cat.name_category}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Prix HT (€) <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="price"
                    step="0.01"
                    value={priceInputValue}
                    required
                    onChange={(e) => setPriceInputValue(e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Récapitulatif estimatif
                  </label>
                  <div className={`w-full rounded-2xl border px-4 py-3 ${isDark ? 'bg-[#0b0b0c] border-white/10 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                    {parsedPriceHT > 0 ? (
                      <div className="space-y-2 text-xs">
                        <div className="flex items-center justify-between gap-4"><span>Montant de vente</span><span className="font-semibold">{parsedPriceHT.toFixed(2)}€</span></div>
                        <div className="flex items-center justify-between gap-4"><span>Part plateforme (5%)</span><span className="text-rose-500">{platformCommission.toFixed(2)}€</span></div>
                        <div className={`flex items-center justify-between gap-4 border-t pt-2 ${isDark ? 'border-white/10' : 'border-gray-200'}`}><span className="font-semibold">Part créateur (95%)</span><span className="font-bold text-emerald-500">{creatorPayout.toFixed(2)}€</span></div>
                        <div className="flex items-center justify-between gap-4"><span className="font-semibold">Reversement Stripe Connect</span><span className="font-bold text-rose-500">{creatorPayout.toFixed(2)}€</span></div>
                        <p className={`pt-1 text-[11px] leading-relaxed ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          Le reversement prévu est de 95% pour le créateur et 5% pour la plateforme. Les obligations fiscales restent à la charge de chaque partie sur sa propre part.
                        </p>
                      </div>
                    ) : (
                      <div className="text-xs text-center opacity-50">Saisissez un prix HT pour voir TVA, commission et estimation Stripe</div>
                    )}
                  </div>
                </div>
                <div className="relative">
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Stock <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="stock"
                    min="0"
                    defaultValue={selectedProduct?.stock ?? '1'}
                    required
                    placeholder="0 = illimité"
                    ref={stockInputRef}
                    className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200'}`}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (stockInputRef.current) {
                        stockInputRef.current.value = 0;
                        stockInputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
                      }
                    }}
                    className={`absolute top-0 right-0 mt-1 mr-1 text-xs px-2 py-1 rounded ${isDark ? 'bg-rose-500/20 text-rose-200 hover:bg-rose-500/30' : 'bg-rose-100 text-rose-700 hover:bg-rose-200'}`}
                  >
                    Illimité
                  </button>
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    0 = Stock illimité
                  </p>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Type de vente <span className="text-rose-500">*</span>
                  </label>
                  <select
                    name="sale_type"
                    value={productSaleType}
                    onChange={(e) => {
                      setProductSaleType(e.target.value);
                      if (e.target.value === 'libre') {
                        setAllowedUsers([]);
                      } else if (e.target.value === 'unique' && allowedUsers.length === 0) {
                        // Pré-sélectionner l'utilisateur actuel si c'est vente unique et personne n'est sélectionné
                        if (user && user.id_user) {
                          setAllowedUsers([String(user.id_user)]);
                        }
                      }
                    }}
                    className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200'}`}
                  >
                    <option value="libre">Libre</option>
                    <option value="unique">Pièce unique</option>
                  </select>
                </div>
              </div>

              {/* UI pour ventes uniques - sélection d'utilisateurs */}
              {productSaleType === 'unique' && (
                <div className={`p-5 rounded-lg border-2 ${isDark ? 'border-rose-500/40 bg-gradient-to-br from-rose-500/10 to-rose-500/5' : 'border-rose-300 bg-gradient-to-br from-rose-50 to-rose-25'}`}>
                  {/* Header avec icône */}
                  <div className="flex items-center gap-2 mb-4">
                    <svg className="w-5 h-5 text-rose-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                    <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
                      Utilisateurs autorisés <span className="text-rose-500">*</span>
                    </h3>
                  </div>

                  {/* Description */}
                  <p className={`text-xs mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    <span className="font-semibold text-rose-500">⚠️ Vente unique :</span> Sélectionnez UN SEUL utilisateur qui pourra voir et acheter cette création exclusive.
                  </p>

                  {/* Bouton désélectionner */}
                  {allowedUsers.length > 0 && (
                    <div className="mb-4">
                      <button
                        type="button"
                        onClick={() => setAllowedUsers([])}
                        className={`text-xs px-3 py-1.5 rounded font-medium transition-all ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                      >
                        ✕ Désélectionner
                      </button>
                    </div>
                  )}

                  {/* Résumé des sélectionnés */}
                  {allowedUsers.length > 0 && (
                    <div className={`mb-4 p-3 rounded-lg ${isDark ? 'bg-rose-500/10 border border-rose-500/30' : 'bg-rose-50 border border-rose-200'}`}>
                      <p className={`text-sm font-semibold ${isDark ? 'text-rose-200' : 'text-rose-700'}`}>
                        ✓ {allowedUsers.length} utilisateur{allowedUsers.length > 1 ? 's' : ''} sélectionné{allowedUsers.length > 1 ? 's' : ''}
                      </p>
                      <div className={`mt-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {allowedUsers.map((userId, idx) => {
                          const user = users.find(u => String(u.id_user || u.id) === userId);
                          const label = user ? (user.nickname || `${user.firstname ?? ''} ${user.lastname ?? ''}`.trim() || user.email) : '';
                          return (
                            <div key={userId} className="text-sm">
                              • {label}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Liste des utilisateurs */}
                  <div className="max-h-60 overflow-y-auto border rounded-lg p-3 pr-2" style={{
                    backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.6)',
                    borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
                  }}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {users.map((u) => {
                        const id = u.id_user || u.id;
                        const label = u.nickname || `${u.firstname ?? ''} ${u.lastname ?? ''}`.trim() || u.email;
                        const checked = allowedUsers.includes(String(id));
                        return (
                          <label key={id} className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            checked 
                              ? isDark 
                                ? 'bg-rose-500/15 border-rose-500/50' 
                                : 'bg-rose-50 border-rose-300'
                              : isDark
                                ? 'bg-white/2 border-white/10 hover:bg-white/5'
                                : 'bg-white/30 border-gray-200 hover:bg-gray-50'
                          }`}>
                            <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                              checked
                                ? isDark
                                  ? 'bg-rose-500/40 border-rose-500'
                                  : 'bg-rose-500 border-rose-500'
                                : isDark
                                  ? 'border-gray-600'
                                  : 'border-gray-300'
                            }`}>
                              {checked && (
                                <svg className={`w-3 h-3 ${isDark ? 'text-rose-200' : 'text-white'}`} fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                            <input
                              type="radio"
                              name="unique_user"
                              checked={checked}
                              onChange={() => {
                                // Pour une vente unique, un seul utilisateur peut être sélectionné
                                setAllowedUsers([String(id)]);
                              }}
                              className="hidden"
                              aria-label={label}
                            />
                            {u.photo && (
                              <img
                                src={u.photo}
                                alt={label}
                                className="w-8 h-8 rounded-full object-cover flex-shrink-0 border-2 border-transparent"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium truncate ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
                                {label}
                              </p>
                              {u.email && (
                                <p className={`text-xs truncate ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                  {u.email}
                                </p>
                              )}
                            </div>
                            {checked && (
                              <div className="flex-shrink-0">
                                <div className={`text-xs font-semibold px-2 py-1 rounded ${isDark ? 'bg-rose-500/40 text-rose-200' : 'bg-rose-100 text-rose-700'}`}>
                                  ✓
                                </div>
                              </div>
                            )}
                          </label>
                        );
                      })}
                      {users.length === 0 && (
                        <div className={`col-span-2 text-center py-4 text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          Aucun utilisateur trouvé
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Avertissement si vide */}
                  {allowedUsers.length === 0 && (
                    <div className={`mt-3 p-3 rounded-lg text-xs ${isDark ? 'bg-yellow-500/10 text-yellow-300 border border-yellow-500/30' : 'bg-yellow-50 text-yellow-700 border border-yellow-200'}`}>
                      ⚠️ Veuillez sélectionner UN utilisateur pour cette vente unique
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Description
                </label>
                <textarea
                  name="description"
                  rows="3"
                  defaultValue={selectedProduct?.description || ''}
                  className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200'}`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Images {productFormMode === 'create' && <span className="text-rose-500">*</span>}
                </label>
                <div className={`border-2 border-dashed rounded-lg p-4 ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImagesUpload}
                    required={productFormMode === 'create' && uploadedImages.length === 0}
                    className={`hidden w-full ${isDark ? 'text-white' : 'text-gray-800'}`}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      isDark 
                        ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                    }`}
                  >
                    Choisir des fichiers
                  </button>
                  {/* Preview des images sélectionnées */}
                  {uploadedImages.length > 0 && (
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
                      {uploadedImages.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <Watermark>
                            <img src={img.preview} alt={`Aperçu ${idx+1}`} className="max-h-32 rounded-lg mx-auto" />
                          </Watermark>
                          <button type="button" onClick={() => handleRemoveUploadedImg(idx)} className="absolute top-1 right-1 bg-rose-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-80 group-hover:opacity-100 transition" title="Supprimer">
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Si édition, afficher les images existantes si aucune nouvelle sélection */}
                  {productFormMode === 'edit' && uploadedImages.length === 0 && selectedProduct?.images && selectedProduct.images.length > 0 && (
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
                      {selectedProduct.images.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <Watermark>
                            <img src={img.path_watermarked ? resolveProductImageUrl({ path_watermarked: img.path_watermarked }) : resolveProductImageUrl({ path_image: img.path_image })} alt={`Image ${idx+1}`} className="max-h-32 rounded-lg mx-auto" />
                          </Watermark>
                          <button
                            type="button"
                            onClick={async () => {
                              const imageId = img?.id_image || img?.id;

                              try {
                                await api.delete(`/creator/products/${selectedProduct.id_product || selectedProduct.id}/image`, {
                                  data: { image_id: imageId }
                                });

                                setSelectedProduct((prev) => {
                                  if (!prev) return prev;

                                  const nextImages = Array.isArray(prev.images)
                                    ? prev.images.filter((currentImg) => (currentImg?.id_image || currentImg?.id) !== imageId)
                                    : [];

                                  return {
                                    ...prev,
                                    images: nextImages,
                                    image: nextImages[0]?.path_watermarked || nextImages[0]?.path_image || null,
                                  };
                                });

                                toast.success('Image supprimée');
                              } catch (err) {
                                toast.error(err?.response?.data?.message || 'Erreur lors de la suppression');
                              }
                            }}
                            className="absolute top-1 right-1 bg-rose-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-80 group-hover:opacity-100 transition"
                            title="Supprimer cette image"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Fichiers telechargeables (zip, pdf, images)
                </label>
                <input
                  type="file"
                  multiple
                  accept=".zip,.pdf,.jpg,.jpeg,.png,.webp"
                  onChange={handleProductFilesChange}
                  className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200'}`}
                />
                {productFiles.length > 0 && (
                  <p className={`text-xs mt-2 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                    {productFiles.length} fichier(s) selectionne(s)
                  </p>
                )}
              </div>

              <div className="flex gap-2 justify-end pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setProductModalOpen(false)}
                  className={`px-4 py-2 rounded-lg border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600"
                >
                  {productFormMode === 'create' ? 'Créer' : 'Modifier'}
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              {selectedProduct && (
                <>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Nom
                    </label>
                    <p>{selectedProduct.name}</p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Catégorie
                    </label>
                    <p>{selectedProduct.category}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Prix HT
                      </label>
                      <p>{selectedProduct.price_ht}€</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Stock
                      </label>
                      <p>{selectedProduct.stock}</p>
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Description
                    </label>
                    <p className={`p-3 rounded-lg ${isDark ? 'bg-[#0b0b0c]' : 'bg-gray-50'}`}>
                      {selectedProduct.description || 'N/A'}
                    </p>
                  </div>
                  {(selectedProduct?.images && selectedProduct.images.length > 0) || selectedProduct?.image ? (
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Galerie
                      </label>
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                        {(selectedProduct?.images?.length > 0
                          ? selectedProduct.images
                          : [selectedProduct]
                        ).slice(0, 4).map((img, index) => {
                          const imageUrl = resolveProductImageUrl(img);
                          if (!imageUrl) return null;

                          return (
                            <Watermark key={`creator-view-image-${index}`}>
                              <img
                                src={imageUrl}
                                alt={`${selectedProduct?.name} ${index + 1}`}
                                className="h-28 w-full rounded-lg object-cover"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            </Watermark>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </>
              )}
              <div className="flex gap-2 justify-end pt-4 border-t">
                <button
                  onClick={() => setProductModalOpen(false)}
                  className={`px-4 py-2 rounded-lg border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
                >
                  Fermer
                </button>
              </div>
            </div>
          )}
        </ModalAdmin>

        {/* Modal Projet */}
        <ModalAdmin
          isOpen={projectModalOpen}
          onClose={() => setProjectModalOpen(false)}
          title={projectFormMode === 'create' ? 'Créer un projet' : 'Modifier le projet'}
          size="lg"
        >
          <form onSubmit={handleSaveProject} className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Liaison du projet <span className="text-rose-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setProjectLinkType('client');
                    setProjectFormData({ ...projectFormData, id_user: '' });
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    projectLinkType === 'client'
                      ? isDark
                        ? 'bg-rose-500/20 border-rose-500 text-rose-200'
                        : 'bg-rose-50 border-rose-300 text-rose-700'
                      : isDark
                        ? 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Client
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const currentUserId = user?.id_user || user?.id;
                    setProjectLinkType('creator');
                    setProjectFormData({ ...projectFormData, id_user: currentUserId || '' });
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    projectLinkType === 'creator'
                      ? isDark
                        ? 'bg-rose-500/20 border-rose-500 text-rose-200'
                        : 'bg-rose-50 border-rose-300 text-rose-700'
                      : isDark
                        ? 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Créateur (moi)
                </button>
              </div>
            </div>

            {projectLinkType === 'client' ? (
              <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Client <span className="text-rose-500">*</span>
              </label>
              <select
                value={projectFormData.id_user}
                onChange={(e) => setProjectFormData({ ...projectFormData, id_user: e.target.value })}
                required
                className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200'}`}
              >
                <option value="">-- Sélectionner un client --</option>
                {users.map((u) => {
                  const id = u.id_user || u.id;
                  const label = u.nickname || `${u.firstname ?? u.first_name ?? ''} ${u.lastname ?? u.name ?? ''}`.trim() || u.email;
                  return (
                    <option key={id} value={id}>
                      {label} ({u.email})
                    </option>
                  );
                })}
              </select>
              </div>
            ) : (
              <div className={`px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-[#0b0b0c] border-white/10 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                Ce projet sera rattaché à votre compte créateur.
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Nom du projet <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={projectFormData.name}
                  onChange={(e) => setProjectFormData({ ...projectFormData, name: e.target.value })}
                  required
                  placeholder="Ex: Logo Serpent"
                  className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200'}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Type
                </label>
                <input
                  type="text"
                  value={projectFormData.type}
                  onChange={(e) => setProjectFormData({ ...projectFormData, type: e.target.value })}
                  placeholder="Ex: Logo, Site web, Application..."
                  className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200'}`}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Statut
                </label>
                <select
                  value={projectFormData.status}
                  onChange={(e) => setProjectFormData({ ...projectFormData, status: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200'}`}
                >
                  <option value="En cours">En cours</option>
                  <option value="En attente">En attente</option>
                  <option value="Terminé">Terminé</option>
                  <option value="Annulé">Annulé</option>
                </select>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Progression ({projectFormData.progress}%)
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={projectFormData.progress}
                  onChange={(e) => setProjectFormData({ ...projectFormData, progress: parseInt(e.target.value) })}
                  className="w-full"
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Description
              </label>
              <textarea
                value={projectFormData.description}
                onChange={(e) => setProjectFormData({ ...projectFormData, description: e.target.value })}
                rows="3"
                placeholder="Détails du projet..."
                className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200'}`}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Date de début
                </label>
                <input
                  type="date"
                  value={projectFormData.start_date}
                  onChange={(e) => setProjectFormData({ ...projectFormData, start_date: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200'}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Date de fin estimée
                </label>
                <input
                  type="date"
                  value={projectFormData.end_date}
                  onChange={(e) => setProjectFormData({ ...projectFormData, end_date: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200'}`}
                />
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-4 border-t">
              <button
                type="button"
                onClick={() => setProjectModalOpen(false)}
                className={`px-4 py-2 rounded-lg border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600"
              >
                {projectFormMode === 'create' ? 'Créer' : 'Modifier'}
              </button>
            </div>
          </form>
        </ModalAdmin>
      </div>
    </div>
  );
};

export default CreatorSpace;
